"use client";

import Link from "next/link";
import { 
  ArrowRight, 
  CheckCircle, 
  ShieldCheck, 
  Server, 
  RefreshCw, 
  MessageSquare, 
  CloudCog, 
  Cpu
} from "lucide-react";
import ServicesGrid from "@/components/ServicesGrid";

export default function AWSPage() {
  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      
      {/* --- HERO HEADER --- */}
      <div className="border-b border-slate-200 bg-white">
        {/* REVERTED SPACING:
           - Back to pt-10 / pt-16 (Standard). 
           - No extra padding needed now that images are balanced.
        */}
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-16 md:pt-16 md:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* LEFT COLUMN: TEXT */}
            <div className="text-left">
              <h1 className="text-4xl md:text-6xl font-bold font-sans text-slate-900 mb-6 flex items-center justify-start gap-0 tracking-tight">
                AWS Solutions
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  className="text-red-600 h-[1em] w-[1.15em] -ml-5"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="3.5" 
                    d="M9 5 l9 7 -9 7" 
                  />
                </svg>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 max-w-xl">
                From migrations and modernization to AI-driven contact centers. We are your go-to AWS powerhouse.
              </p>

              <div className="flex flex-wrap justify-start gap-4">
                <Link 
                  href="/contact-us" 
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-red-600 rounded-full hover:bg-red-700 hover:shadow-lg hover:-translate-y-1"
                >
                  Talk to an Architect
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* RIGHT COLUMN: IMAGE */}
            <div className="flex justify-center lg:justify-end">
              <img 
                src="https://assets.allcode.com/wp-content/uploads/2025/04/AWS-advanced-partners-1.png" 
                alt="AWS Advanced Tier Partner"
                /* max-h-80: Ensures it doesn't get too tall, though badges are usually short. */
                className="w-auto max-h-80 object-contain"
              />
            </div>

          </div>
        </div>
      </div>

      {/* --- SECTION 1: ASSESS & PLAN --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
        <div className="text-center mb-12">
          <span className="text-red-600 font-bold tracking-wider uppercase text-sm">Phase 1</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">Strategic Assessments</h2>
          <p className="text-slate-600 max-w-2xl mx-auto mt-4">
            Don't guess. We validate your architecture against AWS best practices to ensure security, cost-efficiency, and scalability before you write a single line of code.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all">
            <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center mb-6">
              <ShieldCheck className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Well-Architected Review</h3>
            <p className="text-slate-600 mb-4 text-sm leading-relaxed">
              We audit your workload against the 6 pillars of the AWS Well-Architected Framework to close security gaps and optimize costs.
            </p>
            <ul className="space-y-2 text-sm text-slate-500">
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Security Posture Assessment</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Cost Optimization</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all">
            <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center mb-6">
              <CloudCog className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Cloud Readiness & Advisory</h3>
            <p className="text-slate-600 mb-4 text-sm leading-relaxed">
              Whether you are a startup or an enterprise, we evaluate your technical maturity and define the optimal path to the cloud using the Modern CIO's Toolkit.
            </p>
            <ul className="space-y-2 text-sm text-slate-500">
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Migration Readiness Assessment</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Gap Analysis</li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all">
            <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center mb-6">
              <Cpu className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">GenAI Roadmap</h3>
            <p className="text-slate-600 mb-4 text-sm leading-relaxed">
              Cut through the hype. Our GRP Workshop prioritizes high-impact AI use cases, ensuring you invest in ROI-driven features, not science projects.
            </p>
            <ul className="space-y-2 text-sm text-slate-500">
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Feasibility Ranking</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> 90-Minute Strategy Session</li>
            </ul>
          </div>
        </div>
      </div>

      {/* --- SECTION 2: MIGRATE & MODERNIZE --- */}
      <div className="bg-slate-900 pt-8 pb-16 md:pt-10 md:pb-24 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-red-500 font-bold tracking-wider uppercase text-sm">Phase 2</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Migration & Modernization</h2>
            <p className="text-slate-400 max-w-2xl mx-auto mt-4">
              Exit data centers or switch clouds with zero disruption. We execute the "6 R's" of migration to get you to AWS, then modernize with containers and IaC.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <RefreshCw className="h-6 w-6 text-red-500" />
                Migration Engine
              </h3>
              <div className="space-y-6">
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                  <h4 className="font-bold text-lg mb-2">Cross-Cloud & On-Prem</h4>
                  <p className="text-slate-400 text-sm">
                    Specialized expertise in <strong>Azure to AWS</strong> and <strong>GCP to AWS</strong> migrations. We handle the complexity of data transfer, networking, and security mapping.
                  </p>
                </div>
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                  <h4 className="font-bold text-lg mb-2">The 6 R's Strategy</h4>
                  <ul className="grid grid-cols-2 gap-2 text-sm text-slate-400">
                    <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-red-500 rounded-full"></span> Rehost (Lift & Shift)</li>
                    <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-red-500 rounded-full"></span> Replatform (Optimize)</li>
                    <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-red-500 rounded-full"></span> Refactor (Cloud Native)</li>
                    <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-red-500 rounded-full"></span> Repurchase (SaaS)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Server className="h-6 w-6 text-red-500" />
                DevOps & Infrastructure
              </h3>
              <div className="space-y-6">
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                  <h4 className="font-bold text-lg mb-2">Container Orchestration (ECS)</h4>
                  <p className="text-slate-400 text-sm">
                    We modernize legacy applications by containerizing them with <strong>AWS Elastic Container Service (ECS)</strong>. This guarantees portability, scalability, and consistent performance across environments.
                  </p>
                </div>
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
                  <h4 className="font-bold text-lg mb-2">Infrastructure Security & IaC</h4>
                  <p className="text-slate-400 text-sm">
                    Security is code. We deploy immutable infrastructure using <strong>AWS CloudFormation Templates</strong>, ensuring every environment is version-controlled, secure, and repeatable.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* --- SECTION 3: CUSTOMER EXPERIENCE --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-red-600 font-bold tracking-wider uppercase text-sm">Phase 3</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">
              Transform Customer Experience (CX)
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Move beyond legacy PBX. We build omnichannel contact centers that leverage AI to automate support and delight users.
            </p>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Amazon Connect</h3>
                  <p className="text-slate-600 mt-1">
                    Deploy a scalable, cloud-based contact center that integrates voice, chat, and task management in a single interface.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <Cpu className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">AI Voice Bots</h3>
                  <p className="text-slate-600 mt-1">
                    Deflect common queries and automate voicemail with conversational AI agents that understand natural language.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="shrink-0 h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <Server className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">AWS End User Messaging</h3>
                  <p className="text-slate-600 mt-1">
                    Scale your outreach with reliable SMS, push notifications, and email infrastructure designed for high-volume delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-100 rounded-3xl p-8 lg:p-12 border border-slate-200">
             <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-bold text-slate-900">Migration ROI</div>
                  <span className="text-green-600 font-bold">+40% Efficiency</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 mb-2">
                  <div className="bg-red-600 h-2 rounded-full w-[75%]"></div>
                </div>
                <div className="text-xs text-slate-500 text-right">Target Achieved</div>
             </div>

             <div className="bg-white rounded-xl shadow-sm p-6">
                <h4 className="font-bold text-slate-900 mb-2">Why AllCode?</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We don't just lift and shift; we elevate. By combining deep migration expertise with modern DevOps and AI capabilities, we turn your AWS environment into a competitive advantage.
                </p>
             </div>
          </div>
        </div>
      </div>

      <ServicesGrid />

    </div>
  );
}